import axios from "axios";

// Create axios instance with base configuration
const api = axios.create({
  baseURL: import.meta.env.VITE_API_URL || "http://localhost:5000",
  timeout: 30000,
  headers: {
    "Content-Type": "application/json",
  },
});

// Request interceptor to add auth token
api.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem("token");
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  },
);

// Response interceptor for error handling
api.interceptors.response.use(
  (response) => {
    return response;
  },
  (error) => {
    if (error.response?.status === 401) {
      // Don't redirect if we are already on login page or attempting to login/register
      const isAuthPath = window.location.pathname === '/login' || window.location.pathname === '/register';
      const isAuthApi = error.config.url.includes('/login') || error.config.url.includes('/register');
      
      if (!isAuthPath && !isAuthApi) {
        localStorage.removeItem("token");
        window.location.href = "/login";
      }
    }
    return Promise.reject(error);
  },
);

// API service methods
export const apiService = {
  // Generic methods
  get: async (url, config = {}) => {
    return api.get(url, config);
  },
  post: (url, data, config = {}) => api.post(url, data, config),
  put: (url, data, config = {}) => api.put(url, data, config),
  delete: (url, config = {}) => api.delete(url, config),
  patch: (url, data, config = {}) => api.patch(url, data, config),

  // User-specific methods
  users: {
    getAll: () => api.get("/api/users"),
    getById: (id) => api.get(`/api/users/${id}`),
    create: (userData) => api.post("/api/users", userData),
    update: (id, userData) => api.put(`/api/users/${id}`, userData),
    delete: (id) => api.delete(`/api/users/${id}`),
  },

  // Auth methods
  auth: {
    login: (credentials) => api.post("/api/login", credentials),
    register: (userData) => api.post("/api/register", userData),
    logout: () => {
      localStorage.removeItem("token");
      localStorage.removeItem("user");
      return Promise.resolve();
    },
    getCurrentUser: () => {
      const userData = localStorage.getItem("user");
      return userData ? JSON.parse(userData) : null;
    },
    isAuthenticated: () => {
      return !!localStorage.getItem("token");
    },
    isAdmin: () => {
      const user = apiService.auth.getCurrentUser();
      return user && user.role === "admin";
    },
  },

  // Product methods
  products: {
    getAll: (params) => api.get("/api/products", { params }),
    getById: (id) => api.get(`/api/products/${id}`),
    getBySubcategory: (subcategoryId, params) =>
      api.get(`/api/products?subcategory=${subcategoryId}`, { params }),
    getBySubcategoryName: (subcategoryName, params) => {
      // Helper method to get products by subcategory name
      return api.get(`/api/products`, {
        params: {
          ...params,
          subcategory_name: subcategoryName,
        },
      });
    },
    create: (data) => api.post("/api/admin/products", data),
    update: (id, data) => api.put(`/api/admin/products/${id}`, data),
    delete: (id) => api.delete(`/api/admin/products/${id}`),
  },

  // Category methods
  categories: {
    getAll: () => api.get("/api/categories"),
    getById: (id) => api.get(`/api/categories/${id}`),
    create: (data) => api.post("/api/admin/categories", data),
    update: (id, data) => api.put(`/api/admin/categories/${id}`, data),
    delete: (id) => api.delete(`/api/admin/categories/${id}`),
  },

  // Subcategory methods
  subcategories: {
    getAll: (params) => api.get("/api/subcategories", { params }),
    getByCategory: (categoryId) =>
      api.get(`/api/subcategories/category/${categoryId}`),
    getById: (id) => api.get(`/api/subcategories/${id}`),
    create: (data) => api.post("/api/admin/subcategories", data),
    update: (id, data) => api.put(`/api/admin/subcategories/${id}`, data),
    delete: (id) => api.delete(`/api/admin/subcategories/${id}`),
  },

  // Order methods
  orders: {
    getAll: (params) => api.get("/api/orders", { params }),
    getById: (id) => api.get(`/api/orders/${id}`),
    create: (data) => api.post("/api/orders", data),
    update: (id, data) => api.put(`/api/orders/${id}`, data),
    delete: (id) => api.delete(`/api/orders/${id}`),
    getUserOrders: (userId, params) =>
      api.get(`/api/orders/user/${userId}`, { params }),
    getStats: () => api.get("/api/admin/orders/stats/summary"),
  },

  // Invoice methods
  invoices: {
    getAll: (params) => api.get("/api/invoices", { params }),
    getById: (id) => api.get(`/api/invoices/${id}`),
    getByOrderId: (orderId) => api.get(`/api/invoices/by-order/${orderId}`),
    createFromOrder: (orderId, data) =>
      api.post(`/api/invoices/from-order/${orderId}`, data),
    generatePDF: (orderId) => api.post(`/api/invoices/by-order/${orderId}/generate-pdf`),
    downloadPDF: (orderId) => api.get(`/api/invoices/by-order/${orderId}/download-pdf`),
    updateStatus: (orderId, data) => api.put(`/api/invoices/by-order/${orderId}/status`, data),
    exportToExcel: (data) => api.post("/api/invoices/export-excel", data),
    getStats: () => api.get("/api/invoices/stats/summary"),
  },

  // Cart methods
  cart: {
    get: () => api.get("/api/cart"),
    add: (product_id, quantity = 1) =>
      api.post("/api/cart/add", { product_id, quantity }),
    update: (product_id, quantity) =>
      api.put("/api/cart/update", { product_id, quantity }),
    remove: (product_id) =>
      api.delete("/api/cart/remove", { data: { product_id } }),
    clear: () => api.delete("/api/cart/clear"),
  },
};

export default api;
